<template>
  <div>
    <new-consultant-sidebar />
    <save-consultant-basic-information-sidebar
      :callback="updateConsultantDetails"
    />

    <b-row class="justify-content-end">
      <b-col cols="6"><h1 class="mb-3">Informações gerais</h1></b-col>
      <b-col cols="6" v-if="editEnabled" class="text-right mb-3 pr-3">
        <b-button
          variant="outline-primary"
          @click.prevent="goToEditConsultantDetails"
        >
          <feather-icon icon="EditIcon" class="mr-50" />
          <span>Editar</span>
        </b-button>
      </b-col>
    </b-row>

    <div v-if="loading" class="d-flex justify-content-center">
      <b-spinner variant="primary" />
    </div>

    <div v-else-if="details">
      <b-row>
        <b-col md="6" sm="12" class="mb-1">
          <div class="d-flex align-items-center">
            <b-avatar
              v-if="details.picture_file_url"
              size="100"
              variant="light-primary"
              class="badge-minimal mr-1"
              style="cursor: pointer"
            >
              <img
                :src="details.picture_file_url"
                @click.prevent="goToPicture"
                height="100"
              />
            </b-avatar>
            <div
              v-else
              class="pic-wout-img d-flex align-items-center justify-content-center"
            >
              <h1 class="text-weight">
                {{ customerInitials(details)}} 
               </h1>
            </div>
            <div>
              <h3 class="">{{ details.name || "Nome não informado" }}</h3>

              <p>
                {{ details.role || "-" }}
                {{ details.state ? `• ${details.state} ` : "" }}
                {{ details.state_code ? `• ${details.state_code} ` : "" }}
              </p>
              <b-badge v-if="details.is_inactive" variant="warning">
                Inativo
              </b-badge>

            </div>
          </div>
        </b-col>
        <b-col
          md="6"
          sm="12"
          class="text-md-right text-left pr-3 d-flex align-items-center justify-content-end mb-2"
        >
          <div
            v-if="
              summary &&
              (summary.specialist_score_average > 0 ||
                summary.specialist_evaluations_quantity > 0)
            "
          >
            <h4 v-if="summary.specialist_score_average > 0">
              ⭐ {{ summary.specialist_score_average }}
            </h4>
            <p v-if="summary.specialist_evaluations_quantity > 0">
              {{ summary.specialist_evaluations_quantity }} avaliações
            </p>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6" sm="12" class="mt-1">
          <div>
            <small class="text-muted"> E-mail </small>
            <h5 v-if="details.email">
              <a :href="'mailto:' + details.email">
                {{ details.email }}
              </a>
            </h5>
            <h5 v-else>E-mail não informado</h5>
          </div>
        </b-col>
        <b-col md="6" sm="12" class="mt-1">
          <div>
            <small class="text-muted"> CPF </small>
            <h5>
              {{ details.cpf || "-" | cpfCnpj }}
            </h5>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6" sm="12" class="mt-1">
          <div>
            <small class="text-muted"> Celular </small>
            <h5 v-if="details.cell_phone">
              {{ details.cell_phone | formatPhone }}
              <b-button
                class="btn-icon"
                variant="flat"
                style="padding: 0; vertical-align: bottom"
                @click.prevent="goToWhatsapp(details.cell_phone)"
              >
                <feather-icon
                  icon="MessageCircleIcon"
                  size="16"
                  class="text-body"
                  stroke="green"
                />
              </b-button>
            </h5>
            <h5 v-else>Celular não informado</h5>
          </div>
        </b-col>
        <b-col md="6" sm="12" class="mt-1">
          <div>
            <small class="text-muted"> Criado em </small>
            <h5>
              {{ details.created_at | dateTime }}
            </h5>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6" sm="12" class="mt-1">
          <div>
            <small class="text-muted"> Tipo de contrato </small>
            <h5>
              {{ details.contract_type_name || "-" }}
            </h5>
          </div>
        </b-col>
        <b-col md="6" sm="12" class="mt-1">
          <div>
            <small class="text-muted"> Atuação </small>
            <h5>
              {{ details.actuation_label || "-" }}
            </h5>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6" sm="12" class="mt-1">
          <div>
            <small class="text-muted"> Nível </small>
            <h5>
              {{
                details.role +
                  " desde " +
                  (details.role_logs && details.role_logs.length > 0
                    ? formatRoleDate(details.role_logs[0].createdAt)
                    : formatRoleDate(details.created_at)) ||
                "Nível não informado"
              }}
            </h5>
            <template v-if="details.role_logs && details.role_logs.length > 0">
              <h5 v-for="(log, index) in details.role_logs" :key="index">
                <template v-if="details.role_logs[index + 1]">
                  {{
                    log.role_past_name +
                    " de " +
                    formatRoleDate(details.role_logs[index + 1].createdAt) +
                    " a " +
                    formatRoleDate(log.createdAt)
                  }}
                </template>
                <template v-else>
                  {{
                    log.role_past_name +
                    " de " +
                    formatRoleDate(details.created_at) +
                    " a " +
                    formatRoleDate(log.createdAt)
                  }}
                </template>
              </h5>
            </template>
          </div>
        </b-col>
        <b-col md="6" sm="12" class="mt-1">
          <div>
            <small class="text-muted"> Subnível </small>
            <h5 class="mb-0">
              {{ subRoleText }}
            </h5>
            <!-- Código da matriz aprovada: é por ele que a área do Plano de Carreira homologa. -->
            <small v-if="details.sub_role_label" class="text-muted">
              {{ details.sub_role_label }}
            </small>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6" sm="12" class="mt-1">
          <div>
            <small class="text-muted"> Aprovado em </small>
            <h5>
              {{ details.approval_date | onlyDate }}
            </h5>
          </div>
        </b-col>
        <b-col md="6" sm="12" class="mt-1">
          <div>
            <small class="text-muted"> Contrato assinado em </small>
            <h5>
              {{ details.contract_signed_at | dateTime }}
            </h5>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6" sm="12" class="mt-1 mb-3">
          <div>
            <small class="text-muted"> Desativado em </small>
            <h5>
              {{ details.deleted_at | dateTime }}
            </h5>
          </div>
        </b-col>
        <b-col md="6" sm="12" class="mt-1 mb-3">
          <div>
            <small class="text-muted"> Distrato assinado em </small>
            <h5>
              {{ details.contract_termination_signed_at | dateTime }}
            </h5>
          </div>
        </b-col>
      </b-row>

      <!--
        O componente de evolução vive aqui, no perfil, e não na aba "Progressão de nível":
        aquela aba volta para a Minha equipe quando GET /consultants/role_history/{id} responde
        404, o que acontece com quem não tem regra de promoção cadastrada — Franqueado Futuro,
        Founder, colaboradores. A carreira deles sumia junto.
      -->
      <consultant-sub-role-progress-card
        :progress="subRoleProgress"
        :loading="loadingProgress"
        class="mb-2"
      />

      <div>
        <h3>Indicadores de performance</h3>
        <b-row>
          <b-col md="4" sm="12">
            <b-card>
              <b-card-title>Entrevista</b-card-title>
              <b-card-text v-if="interviewData">
                <div class="d-flex align-items-end">
                  <h1 class="mb-0">{{ interviewData.total }}</h1>
                  <span>compromissos</span>
                </div>

                <div class="progress-box">
                  <b-progress
                    :value="interviewData.rate"
                    max="100"
                    class="progress-bar-success"
                    variant="success"
                  />
                  {{ interviewData.rate }}%
                </div>
                taxa de fechamento
              </b-card-text>
              <b-card-text v-else> Dados não disponíveis </b-card-text>
            </b-card>
          </b-col>
          <b-col md="4" sm="12">
            <b-card>
              <b-card-title>MX (Entrega de plano)</b-card-title>
              <b-card-text v-if="xMomentData">
                <div class="d-flex align-items-end">
                  <h1 class="mb-0">{{ xMomentData.total }}</h1>
                  <span>compromissos</span>
                </div>

                <div class="progress-box">
                  <b-progress
                    :value="xMomentData.rate"
                    max="100"
                    class="progress-bar-success"
                    variant="success"
                  />
                  {{ xMomentData.rate }}%
                </div>
                taxa de fechamento
              </b-card-text>
              <b-card-text v-else> Dados não disponíveis </b-card-text>
            </b-card>
          </b-col>
          <b-col md="4" sm="12">
            <b-card>
              <b-card-title>Parceria</b-card-title>
              <b-card-text v-if="partnershipData">
                <div class="d-flex align-items-end">
                  <h1 class="mb-0">{{ partnershipData.total }}</h1>
                  <span>compromissos</span>
                </div>

                <div class="progress-box">
                  <b-progress
                    :value="partnershipData.rate"
                    max="100"
                    class="progress-bar-success"
                    variant="success"
                  />
                  {{ partnershipData.rate }}%
                </div>
                taxa de fechamento
              </b-card-text>
              <b-card-text v-else> Dados não disponíveis </b-card-text>
            </b-card>
          </b-col>
        </b-row>

        <franchise-manual-history-card :user-id="details.user_id" />

        <h3>Destaques qualitativos</h3>
        <div ref="wordCloudContainer" class="word-cloud-container">
          <div
            class="w-100 h-100 d-flex align-items-center justify-content-center"
            v-if="!qualitativeHighlights || qualitativeHighlights.length === 0"
          >
            <h3>Nenhum dado disponível.</h3>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BAvatar,
  BSpinner,
  BButton,
  BPopover,
  BCardText,
  BBadge,
} from "bootstrap-vue";
import { mapActions, mapGetters } from "vuex";
import ConsultantSubRoleProgressCard from "@/modules/consultant/components/ConsultantSubRoleProgressCard";
import * as types from "@/modules/consultant/store/types";
import * as accountTypes from "@/modules/account/store/types";
import { useToast } from "vue-toastification/composition";
import {
  APP_BACKOFFICE_ROLE_ID,
  APP_FINANCIAL_ROLE_ID,
  APP_ADMIN_ROLE_ID,
  APP_MASTER_ROLE_ID,
} from "@/constants/auth";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import SaveConsultantBasicInformationSidebar from "@/modules/consultant/components/SaveConsultantBasicInformationSidebar";
import NewConsultantSidebar from "@/modules/consultant/components/NewConsultantSidebar";
import FranchiseManualHistoryCard from "@/modules/franchise_manual/components/FranchiseManualHistoryCard.vue";
import {
  CLT,
  FRANQUEADO_EXPANSAO,
  FRANQUEADO_PLANEJADOR,
  PJ,
  PREPOSTO,
} from "@/constants/user_contract_types";
import { isPncOrAboveRole } from "@/constants/auth";
import * as affiliationTypes from "@/constants/affiliation_types";
import * as d3 from "d3";

let cloud;
if (typeof window !== "undefined") {
  cloud = require("d3-cloud");
}

export default {
  components: {
    BRow,
    BCol,
    BAvatar,
    BSpinner,
    BButton,
    BPopover,
    BCardText,
    BBadge,
    ConsultantSubRoleProgressCard,
    SaveConsultantBasicInformationSidebar,
    NewConsultantSidebar,
    FranchiseManualHistoryCard,
  },
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      loading: false,
      loadingProgress: false,
      subRoleProgress: null,
      exampleQualitativeHighlights: [
        { name: "Comunicação Agilidade", count: 25 },
        { name: "Agilidade Agilidade", count: 22 },
        { name: "Empatia Agilidade", count: 18 },
        { name: "Profissionalismo Agilidade", count: 20 },
        { name: "Conhecimento Agilidade", count: 19 },
        { name: "Atendimento Agilidade", count: 17 },
        { name: "Dedicado Agilidade", count: 16 },
        { name: "Organizado Agilidade", count: 15 },
        { name: "Proativo Agilidade", count: 21 },
        { name: "Colaborativo Agilidade", count: 14 },
        { name: "Criativo Agilidade", count: 12 },
        { name: "Flexível Agilidade", count: 11 },
        { name: "Pontual Agilidade", count: 10 },
        { name: "Responsável Agilidade", count: 19 },
        { name: "Competente Agilidade", count: 16 },
        { name: "Eficiente Agilidade", count: 15 },
        { name: "Eficaz Agilidade", count: 13 },
        { name: "Dinâmico Agilidade", count: 11 },
        { name: "Inovador Agilidade", count: 9 },
        { name: "Estratégico Agilidade", count: 8 },
        { name: "Liderança Agilidade", count: 17 },
        { name: "Motivado Agilidade", count: 14 },
        { name: "Comprometido Agilidade", count: 18 },
        { name: "Transparente Agilidade", count: 12 },
        { name: "Ético Agilidade", count: 16 },
        { name: "Resiliente", count: 13 },
        { name: "Adaptável", count: 11 },
        { name: "Analítico", count: 10 },
        { name: "Detalhista", count: 14 },
        { name: "Preciso", count: 12 },
        { name: "Persistente", count: 11 },
        { name: "Paciente", count: 9 },
        { name: "Diplomático", count: 8 },
        { name: "Persuasivo", count: 10 },
        { name: "Visionário", count: 7 },
        { name: "Metódico", count: 9 },
        { name: "Sistemático", count: 8 },
        { name: "Prático", count: 13 },
        { name: "Objetivo", count: 15 },
        { name: "Focado", count: 14 },
        { name: "Determinado", count: 12 },
        { name: "Otimista", count: 11 },
        { name: "Energético", count: 10 },
        { name: "Carismático", count: 9 },
        { name: "Inspirador", count: 8 },
        { name: "Solidário", count: 7 },
        { name: "Respeitoso", count: 16 },
        { name: "Cortês", count: 12 },
        { name: "Educado", count: 11 },
        { name: "Gentil", count: 10 },
      ],
    };
  },
  computed: {
    /**
     * "Aguardando validação" é o estado do item 8 do card: o PN ESTÁ numa atuação e ainda falta
     * a evidência de algum critério. Quem não está em atuação nenhuma — Futuro Indica, Gerador
     * de Leads, colaboradores — nunca vai ser enquadrado, e dizer que está aguardando é mentira.
     */
    subRoleText: function () {
      if (!this.details) return "-";
      if (this.details.sub_role_commercial_label) {
        return this.details.sub_role_commercial_label;
      }
      return this.details.actuation_key
        ? "Aguardando validação"
        : "Não faz parte do plano de carreira";
    },

    ...mapGetters({
      details: types.DETAILS_GENERAL_INFORMATION,
      user: accountTypes.USER,
      summary: types.DETAILS_INDICATORS_SUMMARY,
      closureRates: types.DETAILS_APPOINTMENT_CLOSURE_RATES,
      qualitativeHighlights: types.DETAILS_QUALITATIVE_HIGHLIGHTS,
    }),

    editEnabled: function () {
      return (
        this.user?.user_role_id == APP_BACKOFFICE_ROLE_ID ||
        this.user?.user_role_id == APP_FINANCIAL_ROLE_ID ||
        this.user?.user_role_id == APP_ADMIN_ROLE_ID ||
        this.user?.user_role_id == APP_MASTER_ROLE_ID ||
        (this.user?.appify_consultant_id &&
          this.user?.appify_consultant_id === parseInt(this.$route.params.id))
      );
    },

    hasWordCloudData() {
      return (
        this.qualitativeHighlights && this.qualitativeHighlights.length > 0
      );
    },

    interviewData() {
      if (!this.closureRates || !this.closureRates?.interview) {
        return null;
      }

      const interview = this.closureRates?.interview;
      const total = interview?.total || 0;
      const closed = interview?.closed || 0;
      const rate = total > 0 ? Math.round((closed / total) * 100) : 0;

      return {
        total: total,
        closed: closed,
        rate: rate,
      };
    },

    xMomentData() {
      if (!this.closureRates || !this.closureRates.x_moment) {
        return null;
      }

      const xMoment = this.closureRates.x_moment;
      const total = xMoment.total || 0;
      const closed = xMoment.closed || 0;
      const rate = total > 0 ? Math.round((closed / total) * 100) : 0;

      return {
        total: total,
        closed: closed,
        rate: rate,
      };
    },

    partnershipData() {
      if (!this.closureRates || !this.closureRates.partnership) {
        return null;
      }

      const partnership = this.closureRates.partnership;
      const total = partnership.total || 0;
      const closed = partnership.closed || 0;
      const rate = total > 0 ? Math.round((closed / total) * 100) : 0;

      return {
        total: total,
        closed: closed,
        rate: rate,
      };
    },
    isPncPlus() {
      return isPncOrAboveRole(this.user?.user_role_id);
    },
  },
  mounted() {
    this.updateConsultantDetails();
    this.loadSubRoleProgress();
    this.waitForContainer();
  },
  watch: {
    qualitativeHighlights: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.renderWordCloud(newVal);
            }, 300);
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    // A carreira falha em silêncio de propósito: ela é um bloco do perfil, não pode derrubar
    // a ficha inteira se o endpoint de progresso não responder.
    loadSubRoleProgress() {
      this.loadingProgress = true;
      this.getSubRoleProgress(this.$route.params.id)
        .then((response) => {
          this.subRoleProgress = response.data;
        })
        .catch(() => {
          this.subRoleProgress = null;
        })
        .finally(() => {
          this.loadingProgress = false;
        });
    },
    ...mapActions({
      getDetailsGeneralInformation: types.GET_DETAILS_GENERAL_INFORMATION,
      getSubRoleProgress: types.GET_SUB_ROLE_PROGRESS,
      openSaveConsultantBasicInformationSidebar:
        types.OPEN_NEW_CONSULTANT_SIDEBAR,
      openNewConsultantSidebar: types.OPEN_NEW_EMPLOYEE_SIDEBAR,
      getIndicatorsSummary: types.GET_DETAILS_INDICATORS_SUMMARY,
      getAppointmentClosureRates: types.GET_DETAILS_APPOINTMENT_CLOSURE_RATES,
      getQualitativeHighlights: types.GET_DETAILS_QUALITATIVE_HIGHLIGHTS,
    }),
    waitForContainer() {
      const checkContainer = () => {
        if (this.$refs.wordCloudContainer) {
          this.initWordCloud();
        } else {
          setTimeout(checkContainer, 100);
        }
      };

      setTimeout(checkContainer, 100);
    },

    initWordCloud() {
      if (this.qualitativeHighlights && this.qualitativeHighlights.length > 0) {
        this.renderWordCloud(this.qualitativeHighlights);
      }
    },
    updateConsultantDetails() {
      this.loading = true;

      Promise.all([
        this.getDetailsGeneralInformation(this.$route.params.id),
        this.getAppointmentClosureRates(this.$route.params.id),
        this.getQualitativeHighlights(this.$route.params.id),
        this.getIndicatorsSummary(this.$route.params.id),
      ])
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao obter as informações do PN. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    renderWordCloud(wordsData) {
      if (!cloud) {
        return;
      }

      const containerElement = this.$refs.wordCloudContainer;

      if (!containerElement) {
        return;
      }

      const container = d3.select(containerElement);
      container.selectAll("svg").remove();

      const width = containerElement.clientWidth;
      const height = containerElement.clientHeight;

      if (wordsData.length === 0) {
        return;
      }

      const maxCount = d3.max(wordsData, (d) => d.count) || 1;

      const sizeScale = d3
        .scaleLinear()
        .domain([0, maxCount])
        .range([16, 42]);

      const isDark = document.body.classList.contains("dark-layout");

      const lightPalette = [
        "#FF6B00",
        "#FF8A33",
        "#2563EB",
        "#1D4ED8",
        "#38BDF8",
      ];

      const darkPalette = [
        "#FF8A33",
        "#FF6B00",
        "#9CA3AF",
        "#6B7280",
        "#F3F4F6",
      ];

      const palette = isDark ? darkPalette : lightPalette;

      const colorScale = d3
        .scaleOrdinal()
        .domain(wordsData.map((d) => d.name))
        .range(palette);

      const words = wordsData.map((d) => ({
        text: d.name,
        size: sizeScale(d.count),
        count: d.count,
      }));

      try {
        const layout = cloud()
          .size([width - 40, height - 40])
          .words(words)
          .padding(5)
          .rotate(() => {
            const options = [0, -20, 20];
            return options[Math.floor(Math.random() * options.length)];
          })
          .font("Arial")
          .fontSize((d) => d.size)
          .on("end", (words) => {
            this.drawWordCloud(
              words,
              width,
              height,
              colorScale,
              containerElement
            );
          });

        layout.start();
      } catch (error) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao exibir os destaques qualitativos. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        this.renderFallbackCloud(wordsData, width, height, containerElement);
      }
    },

    drawWordCloud(words, width, height, colorScale, containerElement) {
      const container = d3.select(containerElement);

      container.selectAll("svg").remove();

      const svg = container
        .append("svg")
        .attr("width", width)
        .attr("height", height)
        .append("g")
        .attr("transform", `translate(${width / 2},${height / 2})`);

      const centerX = width / 2;
      const centerY = height / 2;
      const margin = 5;

      const validWords = words.filter((d) => {
        const wordWidth = d.text.length * d.size * 0.6;
        const wordHeight = d.size;
        const x = d.x || 0;
        const y = d.y || 0;

        return (
          x + wordWidth / 2 <= centerX - margin &&
          x - wordWidth / 2 >= -centerX + margin &&
          y + wordHeight / 2 <= centerY - margin &&
          y - wordHeight / 2 >= -centerY + margin
        );
      });

      const text = svg
        .selectAll("text")
        .data(validWords)
        .enter()
        .append("text")
        .style("font-size", (d) => `${d.size}px`)
        .style(
          "font-family",
          "'Inter', 'Nunito', 'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, sans-serif"
        )
        .style("font-weight", 600)
        .style("fill", (d) => colorScale(d.text))
        .attr("text-anchor", "middle")
        .attr(
          "transform",
          (d) => `translate(${d.x || 0},${d.y || 0})rotate(${d.rotate || 0})`
        )
        .text((d) => d.text);
    },

    renderFallbackCloud(wordsData, width, height, containerElement) {
      const container = d3.select(containerElement);
      container.selectAll("svg").remove();

      const svg = container
        .append("svg")
        .attr("width", width)
        .attr("height", height);

      const maxCount = d3.max(wordsData, (d) => d.count) || 1;

      const sizeScale = d3
        .scaleLinear()
        .domain([0, maxCount])
        .range([14, 32]);

      const isDark = document.body.classList.contains("dark-layout");

      const lightPalette = [
        "#FF6B00",
        "#FF8A33",
        "#2563EB",
        "#1D4ED8",
        "#38BDF8",
      ];

      const darkPalette = [
        "#FF8A33",
        "#FF6B00",
        "#9CA3AF",
        "#6B7280",
        "#F3F4F6",
      ];

      const palette = isDark ? darkPalette : lightPalette;

      const colorScale = d3
        .scaleOrdinal()
        .domain(wordsData.map((d) => d.name))
        .range(palette);

      const wordsPerRow = 3;
      const wordWidth = width / wordsPerRow;
      const wordHeight = 80;

      wordsData.forEach((word, i) => {
        const row = Math.floor(i / wordsPerRow);
        const col = i % wordsPerRow;

        const x = col * wordWidth + wordWidth / 2;
        const y = row * wordHeight + 50;

        svg
          .append("text")
          .attr("x", x)
          .attr("y", y)
          .attr("text-anchor", "middle")
          .style("font-size", `${sizeScale(word.count)}px`)
          .style("font-family", "Arial, sans-serif")
          .style("font-weight", "bold")
          .style("fill", colorScale(word.count))
          .style("fill", (d) => colorScale(word.name))
      });
    },

    customerInitials(details) {
      const user = [details];

      const userName = user.map((user) => {
        let names = user.name?.split(" ");
        if (names.length == 1) {
          return names[0].charAt(0);
        } else {
          return names[0].charAt(0) + names[names.length - 1].charAt(0);
        }
      });

      return userName[0];
    },
    calculateRate(item) {
      if (!item || item.total <= 0) return 0;
      const rate = (item.closed / item.total) * 100;
      return rate.toFixed(0);
    },
    goToWhatsapp(phone) {
      setTimeout(() => {
        window.open("https://wa.me/" + phone, "_blank").focus();
      });
    },
    goToPicture() {
      setTimeout(() => {
        window.open(this.details.picture_file_url, "_blank").focus();
      });
    },
    goToEditConsultantDetails() {
      if (
        [PREPOSTO, FRANQUEADO_PLANEJADOR, FRANQUEADO_EXPANSAO].includes(
          this.details.user_contract_type_id
        )
      ) {
        this.openSaveConsultantBasicInformationSidebar({
          id: this.$route.params.id,
        });
      } else if ([CLT, PJ].includes(this.details.user_contract_type_id)) {
        this.openNewConsultantSidebar({
          id: this.$route.params.id,
          affiliationType: affiliationTypes.EMPLOYEE,
          saveAction: () => {
            this.updateConsultantDetails();
          },
        });
      } else {
        this.openNewConsultantSidebar({
          id: this.$route.params.id,
          affiliationType: affiliationTypes.NETWORKER,
          saveAction: () => {
            this.updateConsultantDetails();
          },
        });
      }
    },

    formatRoleDate(dateString) {
      const date = new Date(dateString);
      return `${("0" + date.getDate()).slice(-2)}/${(
        "0" +
        (date.getMonth() + 1)
      ).slice(-2)}/${date.getFullYear().toString().slice(-2)}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.pic-wout-img {
  width: 100px;
  height: 100px;
  border-radius: 50px;
  background-color: #6d4042;
  margin-right: 1rem;

  h1 {
    color: #fff;
    font-weight: bold;
    margin-bottom: 0;
    text-transform: uppercase;
  }
}

.word-cloud-container {
  width: 100%;
  padding: 1rem;
  height: 600px;
  border: 1px solid #ccc;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background-color: #ffffff;
}

.dark-layout .word-cloud-container {
  background-color: #111827;
  border-color: #4b5563;
}

.dark-layout .word-cloud-container text {
  paint-order: stroke;
  stroke: rgba(0, 0, 0, 0.4);
  stroke-width: 3px;
}

.word-cloud-container svg {
  display: block;
  margin: 0 auto;
}

.word-cloud-container text {
  transition: all 0.3s ease;
  cursor: pointer;
}

.word-cloud-container text:hover {
  z-index: 1000;
  filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3));
}

.progress-box {
  width: 100%;
  display: flex;
  align-items: center;
  .progress {
    width: 95% !important;
    margin-right: 0.5rem;
  }
}

.card-text {
  div {
    h1 {
      font-weight: bold;
      margin-right: 0.25rem;
    }
  }
}

.dark-layout .card {
  background-color: #2d364f !important;
}
</style>
